import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, from } from 'rxjs';
import { first, map, switchMap, tap } from 'rxjs/operators';
import gql from 'graphql-tag';

import { createDebugLogger, DebugConfig } from '../debug';

const DEBUG_CONFIG: DebugConfig = {
  ENABLED: false,
  LOG_API: false,
  LOG_STATE: false,
};

const FacadeLogger = createDebugLogger('[InverterStatusFacade]', DEBUG_CONFIG);

import {
  Operation,
  GetCommunicationChannelForStickQuery,
  GetLoggerDataQuery,
  SolarmanDataType,
} from 'ngx-core-services';

import { AccessSupportService } from '../../../communication/access-support.service';

import { InverterStatus, OnlineStatus } from './inverter-status.constants';

import {
  extractModuleVersionsFromZCS,
  calculateModuleStatus,
  parseOnlineStatus,
  canConfigureDevice,
  canUpgradeDevice,
  ZCSDeviceData,
} from './inverter-status.utils';

const queryCommunicationChannelForStick = gql`
  fragment communicationchannel on CommunicationChannel {
    CommunicationChannel
    LoggerModel
    LoggerFirmware
    LoggerStatus
  }
`;

const queryLoggerData = gql`
  fragment loggerdatatype on SolarmanDataType {
    loggerId
    devices {
      arm
      dspm
      dsps
      sn
      fota_status
    }
  }
`;

interface InverterUpgradeConfig {
  ARM?: Array<{ LATEST_VERSION?: string; MIN_VERSION?: string }>;
  DSPM?: Array<{ LATEST_VERSION?: string; MIN_VERSION?: string }>;
  DSPS?: Array<{ LATEST_VERSION?: string; MIN_VERSION?: string }>;
}

@Injectable({
  providedIn: 'root',
})
export class InverterStatusFacade {
  private readonly upgradingState = new Map<string, BehaviorSubject<boolean>>();

  constructor(
    private readonly accessSupportService: AccessSupportService,
    private readonly getCommunicationChannelForStickQuery: GetCommunicationChannelForStickQuery,
    private readonly getLoggerDataQuery: GetLoggerDataQuery,
  ) {}

  refreshInverterStatusMinimal(serialNumber: string, loggerId: string | null): Observable<InverterStatus> {
    FacadeLogger.state(`refreshInverterStatusMinimal called for ${serialNumber}, loggerId=${loggerId}`);

    return from(this.fetchStatusMinimal(serialNumber, loggerId)).pipe(
      tap((status) => FacadeLogger.api(`fetchStatusMinimal completed for ${serialNumber}, isOnline=${status.isOnline}`)),
      switchMap((status) => {
        return this.isUpgradeInProgress(serialNumber).pipe(
          first(),
          map((isUpgrading) => ({
            ...status,
            isUpgrading,
            canConfigure: canConfigureDevice(status.isOnline, status.loggerStatus, isUpgrading),
          }))
        );
      }),
      tap((finalStatus) => FacadeLogger.success(`refreshInverterStatusMinimal completed for ${serialNumber}, isOnline=${finalStatus.isOnline}, canConfigure=${finalStatus.canConfigure}, canUpgrade=${finalStatus.canUpgrade}`)),
    );
  }

  setUpgradeInProgress(serialNumber: string, inProgress: boolean): void {
    const subject = this.getOrCreateUpgradingSubject(serialNumber);
    subject.next(inProgress);
  }

  isUpgradeInProgress(serialNumber: string): Observable<boolean> {
    return this.getOrCreateUpgradingSubject(serialNumber).asObservable();
  }

  private getOrCreateUpgradingSubject(serialNumber: string): BehaviorSubject<boolean> {
    if (!this.upgradingState.has(serialNumber)) {
      this.upgradingState.set(serialNumber, new BehaviorSubject<boolean>(false));
    }
    return this.upgradingState.get(serialNumber)!;
  }

  private async fetchStatusMinimal(serialNumber: string, loggerId: string | null): Promise<InverterStatus> {
    FacadeLogger.api(`[${serialNumber}] fetchStatusMinimal START, loggerId=${loggerId}`);

    try {
      if (!loggerId) {
        FacadeLogger.warn(`[${serialNumber}] No loggerId from store - returning unknown status`);
        return this.createUnknownStatus(serialNumber);
      }

      FacadeLogger.api(`[${serialNumber}] Using loggerId from store: ${loggerId}`);

      const [onlineStatus, zcsData, inverterUpgradeConfig] = await Promise.all([
        this.checkOnlineStatus(loggerId),
        this.fetchZCSVersions(loggerId, serialNumber, true),
        this.accessSupportService.getDeviceModuleInfo(serialNumber).catch((e) => {
          FacadeLogger.warn(`[${serialNumber}] getDeviceModuleInfo failed`, e);
          return null;
        }),
      ]);

      FacadeLogger.api(`[${serialNumber}] Online status (ZCS): isOnline=${onlineStatus.isOnline}, channel=${onlineStatus.communicationChannel}`);
      FacadeLogger.api(`[${serialNumber}] ZCS data: arm=${zcsData.arm}, dspm=${zcsData.dspm}, dsps=${zcsData.dsps}, fotaStatus=${zcsData.fotaStatus}`);

      // Get logger config if we have the firmware version
      let loggerLatestVersion: string | undefined;
      const loggerFirmware = onlineStatus.loggerFirmware;
      if (loggerFirmware) {
        try {
          const loggerConfig = await this.accessSupportService.getDeviceModuleInfo(loggerFirmware);
          loggerLatestVersion = (loggerConfig as { LATEST_SOLARMAN_LOGGER_BY_WEB?: string })?.LATEST_SOLARMAN_LOGGER_BY_WEB;
          FacadeLogger.api(`[${serialNumber}] Logger config: loggerFirmware=${loggerFirmware}, loggerLatestVersion=${loggerLatestVersion}`);
        } catch (e) {
          FacadeLogger.warn(`[${serialNumber}] getDeviceModuleInfo for logger failed`, e);
        }
      }

      const isUpgradingFromApi = zcsData.fotaStatus === '1';

      const currentUpgradeState = this.upgradingState.get(serialNumber)?.value ?? false;
      if (isUpgradingFromApi !== currentUpgradeState) {
        this.setUpgradeInProgress(serialNumber, isUpgradingFromApi);
      }

      const config = inverterUpgradeConfig as InverterUpgradeConfig | null;
      const armLatest = config?.ARM?.[0]?.LATEST_VERSION;
      const dspmLatest = config?.DSPM?.[0]?.LATEST_VERSION;
      const dspsLatest = config?.DSPS?.[0]?.LATEST_VERSION;

      const armStatus = calculateModuleStatus(zcsData.arm, armLatest);
      const dspmStatus = calculateModuleStatus(zcsData.dspm, dspmLatest);
      const dspsStatus = calculateModuleStatus(zcsData.dsps, dspsLatest);
      const loggerStatus = calculateModuleStatus(loggerFirmware, loggerLatestVersion, { isLogger: true });

      FacadeLogger.api(`[${serialNumber}] Module statuses: logger=${loggerStatus} (${loggerFirmware} -> ${loggerLatestVersion}), arm=${armStatus} (${zcsData.arm} -> ${armLatest}), dspm=${dspmStatus} (${zcsData.dspm} -> ${dspmLatest}), dsps=${dspsStatus} (${zcsData.dsps} -> ${dspsLatest})`);

      const status: InverterStatus = {
        serialNumber,
        loggerSn: loggerId,
        isOnline: onlineStatus.isOnline,
        communicationChannel: onlineStatus.communicationChannel,

        loggerVersion: loggerFirmware,
        armVersion: zcsData.arm,
        dspmVersion: zcsData.dspm,
        dspsVersion: zcsData.dsps,

        loggerLatestVersion,
        armLatestVersion: armLatest,
        dspmLatestVersion: dspmLatest,
        dspsLatestVersion: dspsLatest,

        loggerStatus,
        armStatus,
        dspmStatus,
        dspsStatus,

        canUpgrade: canUpgradeDevice(onlineStatus.isOnline),
        canConfigure: false,
        isUpgrading: isUpgradingFromApi,

        lastUpdated: new Date(),
      };

      FacadeLogger.success(`[${serialNumber}] fetchStatusMinimal COMPLETE: isOnline=${status.isOnline}, isUpgrading=${status.isUpgrading}, arm=${status.armStatus}, dspm=${status.dspmStatus}, dsps=${status.dspsStatus}`);

      return status;
    } catch (error) {
      FacadeLogger.error(`[${serialNumber}] fetchStatusMinimal FAILED`, error);
      return this.createUnknownStatus(serialNumber, loggerId ?? '-');
    }
  }

  private createUnknownStatus(serialNumber: string, loggerSn = '-'): InverterStatus {
    return {
      serialNumber,
      loggerSn,
      isOnline: false,
      communicationChannel: null,

      loggerStatus: 'unknown',
      armStatus: 'unknown',
      dspmStatus: 'unknown',
      dspsStatus: 'unknown',

      canUpgrade: false,
      canConfigure: false,
      isUpgrading: false,

      lastUpdated: new Date(),
    };
  }

  private async checkOnlineStatus(loggerId: string): Promise<OnlineStatus & { loggerFirmware?: string }> {
    try {
      const result = await this.getCommunicationChannelForStickQuery.execute(
        queryCommunicationChannelForStick,
        { loggerId, operation: Operation.AGGIORNAMENTO },
        true
      );

      const data = result.data?.getCommunicationChannelForStick;
      const loggerFirmware = data?.LoggerFirmware;

      FacadeLogger.api(`[${loggerId}] checkOnlineStatus - LoggerFirmware from API: ${loggerFirmware}`);

      return { ...parseOnlineStatus(data), loggerFirmware };
    } catch {
      return { isOnline: null, communicationChannel: null };
    }
  }

  private async fetchZCSVersions(
    deviceSn: string | undefined,
    inverterId: string,
    isOnline: boolean | null
  ): Promise<{ arm?: string; dspm?: string; dsps?: string; fotaStatus?: string | null }> {
    if (isOnline !== true || !deviceSn) {
      return {};
    }

    try {
      const loggerData = await this.getLoggerDataQuery.execute(
        queryLoggerData,
        { id: deviceSn },
        true
      );

      const devices = (loggerData?.data?.getLoggerData as SolarmanDataType)?.devices;
      const inverterProps = devices?.find((x) => x.sn === inverterId) as ZCSDeviceData | undefined;

      const versions = extractModuleVersionsFromZCS(inverterProps);
      const fotaStatus = (inverterProps as unknown as { fota_status?: string | null })?.fota_status;

      return { ...versions, fotaStatus };
    } catch {
      return {};
    }
  }
}
